import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Clock, Link as LinkIcon, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSite, useUrl } from "@/context/SiteContext";
import { SmartLink } from "@/components/SmartLink";

function readingTime(text = "") {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

const mdComponents = {
  h1: (props) => (
    <h1 className="font-display font-medium uppercase text-4xl md:text-5xl tracking-tighter mt-16 mb-6 leading-[0.95]" {...props} />
  ),
  h2: (props) => (
    <h2 className="font-display font-medium uppercase text-2xl md:text-3xl tracking-tight mt-14 mb-5 leading-tight border-b border-black pb-3" {...props} />
  ),
  h3: (props) => (
    <h3 className="font-display font-bold uppercase text-lg md:text-xl tracking-tight mt-10 mb-3" {...props} />
  ),
  p: (props) => (
    <p className="text-sm md:text-[15px] leading-[1.7] text-black/85 mb-5" {...props} />
  ),
  strong: (props) => <strong className="font-bold text-black" {...props} />,
  em: (props) => <em className="italic" {...props} />,
  ul: (props) => <ul className="my-6 border-t border-black" {...props} />,
  ol: (props) => <ol className="my-6 border-t border-black list-decimal" {...props} />,
  li: ({ ordered, ...props }) => (
    <li className="border-b border-black py-3 pl-8 relative text-sm md:text-[15px] leading-relaxed text-black/85 before:content-['→'] before:absolute before:left-1 before:top-3 before:font-mono before:text-black/60" {...props} />
  ),
  blockquote: (props) => (
    <blockquote className="my-8 border-l-4 border-black pl-6 py-2 italic text-sm md:text-[15px] text-black/80 bg-neutral-50" {...props} />
  ),
  a: (props) => (
    <a className="underline underline-offset-4 hover:bg-black hover:text-white transition-all" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  hr: () => <hr className="my-12 border-black" />,
  code: (props) => <code className="font-mono bg-black text-white px-2 py-0.5 text-sm" {...props} />,
};

export function BlogListPage() {
  const [articles, setArticles] = useState(null);
  const url = useUrl();
  const { t } = useSite();
  useEffect(() => {
    api.get("/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  const featured = articles && articles.length > 0 ? articles[0] : null;
  const rest = articles && articles.length > 1 ? articles.slice(1) : [];

  return (
    <div data-testid="blog-list-page">
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
          <div className="font-mono-label text-black/60">{t("section.blog.hero.eyebrow")}</div>
          <h1 className="mt-4 font-display font-medium uppercase text-5xl md:text-8xl tracking-tighter leading-[0.9]">
            {t("section.blog.hero.title_1")}<br /><span className="italic font-normal">{t("section.blog.hero.title_2")}</span> {t("section.blog.hero.title_3")}
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-14">
        {articles === null && (
          <div className="font-mono-label text-black/60" data-testid="blog-loading">Se încarcă...</div>
        )}
        {articles && articles.length === 0 && (
          <div className="border border-black p-12 text-center" data-testid="blog-empty">
            <div className="font-mono-label text-black/60">Curând</div>
            <div className="mt-3 font-display font-medium uppercase text-3xl">Primele articole vor apărea în curând.</div>
          </div>
        )}

        {/* Featured article */}
        {featured && (
          <Link to={url.article(featured.slug)} data-testid="featured-article"
            className="group grid grid-cols-1 lg:grid-cols-12 gap-8 border-y border-black py-10 mb-6 hover:bg-black hover:text-white transition-all px-4 md:px-6">
            <div className="lg:col-span-7 order-2 lg:order-1 flex flex-col justify-between">
              <div>
                <div className="font-mono-label opacity-60 flex items-center gap-4">
                  <span>[ FEATURED · CEL MAI RECENT ]</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {readingTime(featured.content)} min</span>
                </div>
                <div className="mt-4 font-display font-medium uppercase text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                  {featured.title}
                </div>
                <p className="mt-5 opacity-70 text-sm md:text-[15px] max-w-3xl leading-relaxed">{featured.excerpt}</p>
              </div>
              <div className="mt-6 flex items-center justify-between">
                <div className="font-mono-label opacity-60">{formatDate(featured.created_at)}</div>
                <div className="font-mono-label inline-flex items-center gap-2 group-hover:gap-4 transition-all">
                  Citește articolul <ArrowRight size={14} />
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2">
              {featured.cover_image && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title}
                    className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
            </div>
          </Link>
        )}

        {/* Rest */}
        {rest.length > 0 && (
          <div className="border-t border-black" data-testid="blog-list">
            {rest.map((a) => (
              <Link key={a.id} to={url.article(a.slug)} data-testid={`article-${a.slug}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8 border-b border-black hover:bg-black hover:text-white transition-all px-4 group">
                <div className="md:col-span-2 font-mono-label opacity-60 flex flex-col gap-1">
                  <span>{formatDate(a.created_at)}</span>
                  <span className="inline-flex items-center gap-1 text-[10px]"><Clock size={10} /> {readingTime(a.content)} min</span>
                </div>
                <div className="md:col-span-7">
                  <div className="font-display font-medium uppercase text-2xl md:text-4xl tracking-tighter leading-[0.95]">{a.title}</div>
                  <div className="mt-3 opacity-70 max-w-2xl text-sm">{a.excerpt}</div>
                </div>
                <div className="md:col-span-3">
                  {a.cover_image ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={a.cover_image} alt={a.title}
                        className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="text-right font-mono-label opacity-60 mt-4">Citește →</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ShareLink() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const el = document.createElement("textarea");
      el.value = window.location.href;
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e2) { /* ignore */ }
      document.body.removeChild(el);
    }
  };
  return (
    <button onClick={copy} data-testid="copy-link-btn"
      className="font-mono-label inline-flex items-center gap-2 border border-black px-4 py-2 hover:bg-black hover:text-white transition-all">
      {copied ? <><Check size={14} /> Link copiat</> : <><LinkIcon size={14} /> Copiază link</>}
    </button>
  );
}

/**
 * Pick the most semantically-related articles for the "Continuă lectura"
 * section. Uses a lightweight keyword-overlap score between the current
 * article's title+excerpt and every other article's title+excerpt.
 * Ties fall back to recency; empty scores fill from most recent.
 */
const RO_STOPWORDS = new Set([
  "si","sau","dar","din","cu","de","la","in","pe","pentru","o","un","al","ale",
  "cel","cei","cea","cele","este","sunt","a","ca","fie","daca","nu","se","va",
  "asa","asa-numit","doar","tot","toate","toti","toate","catre","mai","poate",
  "ai","ii","le","lui","lor","dupa","fara","spre","despre","suntec","peste",
  "prin","intr","intr-o","intr-un","acest","aceasta","acestia","acestea",
  "care","cand","unde","cum","ce","fost","era","fiind","sub","pana","asa",
]);

function tokenize(str) {
  if (!str) return [];
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3 && !RO_STOPWORDS.has(w));
}

function pickRelated(current, all, count = 3) {
  const others = (all || []).filter((a) => a.slug !== current.slug);
  if (!others.length) return [];
  const currentTokens = new Set(tokenize(`${current.title} ${current.excerpt || ""}`));
  const scored = others.map((a) => {
    const tokens = tokenize(`${a.title} ${a.excerpt || ""}`);
    let overlap = 0;
    for (const tk of tokens) if (currentTokens.has(tk)) overlap++;
    return { article: a, score: overlap };
  });
  scored.sort((x, y) => {
    if (y.score !== x.score) return y.score - x.score;
    // Fallback: newest first
    return (y.article.created_at || "").localeCompare(x.article.created_at || "");
  });
  return scored.slice(0, count).map((s) => s.article);
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const url = useUrl();
  const { t } = useSite();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setError(false);
    (async () => {
      try {
        const [detailRes, listRes] = await Promise.all([
          api.get(`/articles/${slug}`),
          api.get("/articles"),
        ]);
        if (cancelled) return;
        setArticle(detailRes.data);
        setAllArticles(listRes.data);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Scroll to top on slug change
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [slug]);

  // Inject SEO meta tags (title, description, og:image) when the article loads
  useEffect(() => {
    if (!article) return;
    const title = article.meta_title || article.title;
    const desc = article.meta_description || article.excerpt || "";
    const ogImg = article.og_image || article.cover_image || "";
    const prevTitle = document.title;
    document.title = title ? `${title} — SUNTEC` : prevTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!value) { if (el) el.remove(); return null; }
      if (!el) {
        el = document.createElement("meta");
        const [key, val] = selector.replace(/^meta\[/, "").replace(/\]$/, "").split("=");
        el.setAttribute(key, val.replace(/["']/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
      return el;
    };

    const created = [
      setMeta('meta[name="description"]', "content", desc),
      setMeta('meta[property="og:title"]', "content", title),
      setMeta('meta[property="og:description"]', "content", desc),
      setMeta('meta[property="og:image"]', "content", ogImg),
      setMeta('meta[property="og:type"]', "content", "article"),
      setMeta('meta[name="twitter:card"]', "content", "summary_large_image"),
      setMeta('meta[name="twitter:image"]', "content", ogImg),
    ].filter(Boolean);

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el && el.parentNode && el.parentNode.removeChild(el));
    };
  }, [article]);

  if (error) return (
    <div className="mx-auto max-w-[1440px] px-6 py-24 text-center" data-testid="article-not-found">
      <div className="font-display font-medium uppercase text-4xl">Articol negăsit</div>
      <Link to={url.blog} className="mt-6 inline-block font-mono-label underline">Înapoi la blog</Link>
    </div>
  );

  if (!article) return <div className="p-12 font-mono-label text-black/60" data-testid="article-loading">Se încarcă...</div>;

  const idx = allArticles.findIndex((a) => a.slug === article.slug);
  const prev = idx > 0 ? allArticles[idx - 1] : null;
  const next = idx >= 0 && idx < allArticles.length - 1 ? allArticles[idx + 1] : null;
  const related = pickRelated(article, allArticles, 3);
  const minutes = readingTime(article.content);
  const isHtml = /<[a-z][\s\S]*>/i.test(article.content || "");

  return (
    <article data-testid={`article-detail-${article.slug}`}>
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 pt-10">
        <Link to={url.blog} data-testid="back-to-blog" className="font-mono-label inline-flex items-center gap-2 hover:underline">
          <ArrowLeft size={14} /> Toate articolele
        </Link>
      </div>

      <section className="mx-auto max-w-[900px] px-6 md:px-10 py-14 border-b border-black">
        <div className="font-mono-label text-black/60 flex flex-wrap items-center gap-4">
          <span>{new Date(article.created_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "long", year: "numeric" })}</span>
          <span className="opacity-30">/</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {minutes} min de citit</span>
        </div>
        <h1 className="mt-4 font-display font-medium uppercase text-4xl md:text-6xl tracking-tighter leading-[0.95]">{article.title}</h1>
        <p className="mt-6 text-base text-black/70">{article.excerpt}</p>
        <div className="mt-8"><ShareLink /></div>
      </section>

      {article.cover_image && (
        <div className="relative h-[40vh] md:h-[60vh] border-b border-black overflow-hidden">
          <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover grayscale-img" />
        </div>
      )}

      <section className="mx-auto max-w-[780px] px-6 py-16">
        {isHtml ? (
          <div
            className="prose prose-neutral prose-lg max-w-none"
            data-testid="article-html"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {article.content}
          </ReactMarkdown>
        )}

        {/* share at bottom too */}
        <div className="mt-14 pt-8 border-t border-black flex flex-wrap items-center justify-between gap-4">
          <div className="font-mono-label text-black/60">Ți-a plăcut? Trimite mai departe.</div>
          <ShareLink />
        </div>
      </section>

      {/* Prev / Next */}
      {(prev || next) && (
        <section className="grid grid-cols-2 border-y border-black" data-testid="prev-next-nav">
          {prev ? (
            <Link to={url.article(prev.slug)} data-testid="prev-article"
              className="p-6 md:p-10 border-r border-black hover:bg-black hover:text-white transition-all">
              <div className="font-mono-label opacity-60 flex items-center gap-2"><ArrowLeft size={12} /> Articolul anterior</div>
              <div className="mt-3 font-display font-medium uppercase text-lg md:text-2xl tracking-tight leading-tight">{prev.title}</div>
            </Link>
          ) : (
            <div className="p-6 md:p-10 border-r border-black opacity-30">
              <div className="font-mono-label">Nu există articol anterior</div>
            </div>
          )}
          {next ? (
            <Link to={url.article(next.slug)} data-testid="next-article"
              className="p-6 md:p-10 hover:bg-black hover:text-white transition-all text-right">
              <div className="font-mono-label opacity-60 flex items-center justify-end gap-2">Articolul următor <ArrowRight size={12} /></div>
              <div className="mt-3 font-display font-medium uppercase text-lg md:text-2xl tracking-tight leading-tight">{next.title}</div>
            </Link>
          ) : (
            <div className="p-6 md:p-10 opacity-30 text-right">
              <div className="font-mono-label">Nu există articol următor</div>
            </div>
          )}
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16" data-testid="related-articles">
          <div className="font-mono-label text-black/60">Articole recomandate</div>
          <h2 className="mt-3 font-display font-medium uppercase text-3xl md:text-5xl tracking-tighter">Continuă lectura</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-black border border-black">
            {related.map((a) => (
              <Link key={a.id} to={url.article(a.slug)} data-testid={`related-${a.slug}`}
                className="group bg-white hover:bg-black hover:text-white transition-all p-6 flex flex-col">
                {a.cover_image && (
                  <div className="aspect-[16/10] overflow-hidden mb-4">
                    <img src={a.cover_image} alt={a.title}
                      className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="font-mono-label opacity-60 flex items-center gap-3">
                  <span>{formatDate(a.created_at)}</span>
                  <span className="inline-flex items-center gap-1 text-[10px]"><Clock size={10} /> {readingTime(a.content)} min</span>
                </div>
                <div className="mt-3 font-display font-medium uppercase text-lg md:text-xl tracking-tight leading-tight flex-1">{a.title}</div>
                <div className="mt-4 font-mono-label inline-flex items-center gap-2 group-hover:gap-4 transition-all">Citește →</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-14 py-16 border-t border-black">
        <SmartLink to={t("btn.blog.article.cta.link", url.contact)} data-testid="article-cta-contact" className="inline-flex items-center gap-3 font-mono-label bg-black text-white px-6 py-4 border border-black hover:bg-white hover:text-black">
          {t("btn.blog.article.cta.label")} <ArrowRight size={16} />
        </SmartLink>
      </section>
    </article>
  );
}
